import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useMediaQuery,
  useTheme,
  Chip,
  Tooltip,
  Stack,
} from "@mui/material";
import {
  CalendarMonth as CalendarMonthIcon,
  PictureAsPdf as PictureAsPdfIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  Print as PrintIcon,
  Place as PlaceIcon,
  Info as InfoIcon,
  PlayCircle as PlayCircleIcon,
} from "@mui/icons-material";
import PageLayout from "../components/Common/PageLayout";
import PageBanner from "../components/Common/PageBanner";
import Pagination from "../components/Common/Pagination";
import CalendarAdd from "@smfa/utils/src/components/CalendarAdd";
// eslint-disable-next-line no-unused-vars
import { getPaginatedItems, formatDateTime } from "../utils/helper";
import {
  getMeetingDocument,
  getMeetings,
} from "../actions/pagecontent.actions";
import { toString } from "lodash";
import UpsellOrLogin from "../components/Common/UpsellOrLogin";
import { hasMembership } from "@smfa/utils/src/store/slices/userSlice";
import VideoEmbed from "../components/Common/VideoEmbed";

/**
 * Reusable Meeting Card component
 * @param {Object} props - Component props
 * @param {Object} props.meeting - Meeting data
 * @param {string} props.variant - Card variant ('featured', 'upcoming', or 'past')
 * @param {Function} props.onViewDetails - Function to view meeting details
 * @param {Function} props.onDownloadDocument - Function to download document
 * @param {Function} props.formatDate - Date formatting function
 * @param {Function} props.getCalendarEvent - Function to get calendar event
 * @returns {JSX.Element}
 */
const MeetingCard = ({
  meeting,
  variant = "upcoming",
  onViewDetails,
  onDownloadDocument,
  formatDate,
  getCalendarEvent,
}) => {
  // Determine if meeting is upcoming based on start date
  const isUpcoming = new Date(meeting.meeting_start_dt) > new Date();
  const isFeatured = variant === "featured";
  const isPast = variant === "past";
  const meetingYear = new Date(meeting.meeting_start_dt).getFullYear();

  // Set variant-specific styling
  const variantStyles = {
    featured: {
      borderLeft: "5px solid #1976d2",
      dateBgColor: "primary.main",
      textColor: "text.primary",
      paperElevation: 3,
    },
    upcoming: {
      borderLeft: "5px solid #1976d2",
      dateBgColor: "primary.main",
      textColor: "text.primary",
      paperElevation: 2,
    },
    past: {
      borderLeft: "5px solid #e68d14",
      dateBgColor: "#e68d14",
      textColor: "text.secondary",
      paperElevation: 2,
    },
  }[variant];

  const auth = useSelector((state) => state.auth);
  const { data: userData = {} } = useSelector((state) => state.user);
  const requireMembership = true;
  const hasMembershipVal = !requireMembership ? true : hasMembership(userData);

  // Local state for paywall dialog
  const [paywallOpen, setPaywallOpen] = useState(false);

  return (
    <Paper
      elevation={variantStyles.paperElevation}
      sx={{
        p: 3,
        borderRadius: 2,
        mb: 3,
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "center",
        gap: 2,
        borderLeft: variantStyles.borderLeft,
        boxShadow: isFeatured ? "0px 4px 10px rgba(0,0,0,0.1)" : undefined,
      }}
      className={isPast ? "prev-sec-inner" : ""}
    >
      {/* Date/Thumbnail Vertical Stack */}
      <Stack
        direction="column"
        spacing={2}
        alignItems="center"
        sx={{ width: 120, minWidth: 120 }}
        onClick={() => {
          onViewDetails(meeting);
        }}
      >
        {/* Date Display */}
        <Box
          sx={{
            bgcolor: variantStyles.dateBgColor,
            color: "white",
            p: 2,
            borderRadius: 2,
            textAlign: "center",
            width: 120,
            minWidth: 120,
          }}
          className={isPast ? "prev-meet-sec-left" : ""}
        >
          <Typography variant="h6" component="h3">
            {new Date(meeting.meeting_start_dt).toLocaleDateString("en-US", {
              month: "short",
            })}
          </Typography>
          <Typography variant="body1">
            {new Date(meeting.meeting_start_dt).toLocaleDateString("en-US", {
              year: "numeric",
            })}
          </Typography>
        </Box>
        {/* Thumbnail image styled like date box but with different color */}
        {meeting.meeting_image && (
          <Box
            sx={{
              p: 0,
              textAlign: "center",
              width: 120,
              minWidth: 120,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              src={`${process.env.REACT_APP_IMAGE_URL}/${meeting.meeting_image}`}
              alt={meeting.meeting_title}
              style={{
                width: "100%",
                height: "auto",
                objectFit: "cover",
                borderRadius: 4,
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                border: `1px solid ${variantStyles.dateBgColor}`,
              }}
            />
          </Box>
        )}
      </Stack>
      {/* Content Container */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          flexGrow: 1,
          width: { xs: "100%", sm: "auto" },
          gap: 3,
          minWidth: 0,
        }}
      >
        {/* Meeting Details */}
        <Box
          sx={{
            flexGrow: 1,
            width: { xs: "100%", sm: isFeatured ? "60%" : "auto" },
            minWidth: 0,
          }}
          className={isPast ? "prev-meet-sec-right" : ""}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              mb: 1,
            }}
          >
            <Typography
              variant={isFeatured ? "h5" : "h6"}
              component="h4"
              sx={{ mr: 2, mb: 1 }}
            >
              {meeting.meeting_title}
            </Typography>

            {!isPast && (
              <Tooltip title="Add to calendar">
                <span>
                  <CalendarAdd
                    event={getCalendarEvent(meeting)}
                    buttonProps={{ color: "primary" }}
                  />
                </span>
              </Tooltip>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1,
              color: variantStyles.textColor,
            }}
          >
            <CalendarMonthIcon
              sx={{ mr: 1 }}
              fontSize={isFeatured ? "small" : "small"}
            />
            <Typography variant="body2">
              {formatDate(meeting.meeting_start_dt)} to{" "}
              {formatDate(meeting.meeting_end_dt)}
            </Typography>
          </Box>

          {meeting.meeting_location && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 2,
                color: variantStyles.textColor,
              }}
            >
              <PlaceIcon
                sx={{ mr: 1 }}
                fontSize={isFeatured ? "small" : "small"}
              />
              <Typography variant="body2">
                {meeting.meeting_location}
              </Typography>
            </Box>
          )}

          <Typography
            variant={isFeatured ? "body1" : "body2"}
            color={variantStyles.textColor}
            component="div"
            sx={{
              maxHeight: isFeatured ? "none" : "80px",
              overflow: isFeatured ? "visible" : "hidden",
              textOverflow: isFeatured ? "clip" : "ellipsis",
              display: isFeatured ? "block" : "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              mt: isFeatured ? 2 : 0,
            }}
            dangerouslySetInnerHTML={{
              __html: meeting.meeting_content
                ? isFeatured
                  ? meeting.meeting_content.substring(0, 500) +
                    (meeting.meeting_content.length > 500 ? "..." : "")
                  : meeting.meeting_content
                      .replace(/<[^>]+>/g, " ")
                      .substring(0, 200) + "..."
                : "",
            }}
          />

          {/* Extra content for featured view */}
          {isFeatured && (
            <>
              {meeting.meeting_content?.length > 500 && (
                <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                  <Button
                    variant="text"
                    color="primary"
                    endIcon={<ChevronRightIcon />}
                    onClick={() => onViewDetails(meeting)}
                  >
                    View Full Details
                  </Button>
                </Box>
              )}

              {isUpcoming && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                  <Button
                    component={Link}
                    to={`/register?year=${meetingYear}`}
                    variant="contained"
                    color="primary"
                    size="large"
                    className="regis-btn default-btn"
                  >
                    Register Now
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Image for featured view - Deprecated in favor of thumbnail image below the date*/}
        {/* {isFeatured && meeting.meeting_image && (
          <Box
            sx={{
              width: { xs: "100%", sm: "40%" },
              display: { xs: "none", sm: "block" },
            }}
          >
            <Card sx={{ borderRadius: 2, overflow: "hidden", height: "100%" }}>
              <CardMedia
                component="img"
                image={`${process.env.REACT_APP_IMAGE_URL}/${meeting.meeting_image}`}
                alt={meeting.meeting_title}
                sx={{ height: 200, objectFit: "cover" }}
              />
            </Card>
          </Box>
        )} */}

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 1,
            mt: { xs: 2, sm: 0 },
            width: { xs: "100%", sm: isPast ? 170 : "auto" },
            minWidth: { xs: 0, sm: isPast ? 170 : 0 },
            flexShrink: 0,
          }}
        >
          {/* Details Button */}
          <Stack direction="column" spacing={1} sx={{ width: "100%" }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => onViewDetails(meeting)}
              endIcon={<InfoIcon />}
              size={!isPast ? "medium" : "small"}
              fullWidth={isPast}
            >
              Details
            </Button>
            {/* Recap Button (only for past meetings) with paywall */}
            {isPast &&
              (meeting.meeting_video &&
              String(meeting.meeting_video).trim() !== "" ? (
                hasMembershipVal ? (
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<PlayCircleIcon />}
                    onClick={() => onViewDetails(meeting)}
                    sx={{ mt: 1 }}
                    size="small"
                    fullWidth
                  >
                    Recap
                  </Button>
                ) : (
                  <Tooltip title="Members only: Sign up or log in to watch the recap video">
                    <span>
                      <Button
                        variant="outlined"
                        color="success"
                        startIcon={<PlayCircleIcon />}
                        sx={{ mt: 1 }}
                        size="small"
                        onClick={() => setPaywallOpen(true)}
                        fullWidth
                      >
                        Recap
                      </Button>
                    </span>
                  </Tooltip>
                )
              ) : (
                <Tooltip title="Meeting recap video not available/uploaded yet">
                  <span>
                    <Button
                      variant="outlined"
                      color="success"
                      startIcon={<PlayCircleIcon />}
                      disabled
                      sx={{ mt: 1 }}
                      size="small"
                      fullWidth
                    >
                      Recap
                    </Button>
                  </span>
                </Tooltip>
              ))}
          </Stack>

          {/* Document Download Button (if available and past meeting) */}
          {meeting.meeting_documents && (
            <Button
              variant={isPast ? "contained" : "outlined"}
              color="primary"
              startIcon={<PictureAsPdfIcon />}
              onClick={() =>
                onDownloadDocument(
                  meeting.meeting_id,
                  meeting.meeting_documents,
                )
              }
              sx={{
                width: "100%",
                minWidth: 0,
                whiteSpace: "nowrap",
                display: isFeatured && !isPast ? "none" : "flex",
              }}
              size={isFeatured ? "medium" : "small"}
              fullWidth={isPast}
            >
              {isPast ? "Download PDF" : "PDF"}
            </Button>
          )}
        </Box>
      </Box>
      {/* Paywall Dialog */}
      <UpsellOrLogin
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        isAuthenticated={auth?.isAuthenticated}
        requireMembership={requireMembership}
        hasMembership={hasMembershipVal}
        featureName="Meeting Recap Video"
      />
    </Paper>
  );
};

// Utility: Check if recap is available for a meeting
const isRecapAvailable = (meeting) => {
  const now = new Date();
  return (
    meeting &&
    meeting.meeting_video &&
    new Date(meeting.meeting_end_dt) < now &&
    String(meeting.meeting_video).trim() !== ""
  );
};

/**
 * Meeting page component
 * @returns {JSX.Element}
 * @component
 */
const Meeting = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const printRef = useRef();

  // Redux state
  const meetings = useSelector((state) => state.pageContents?.meetings || []);
  const { meetingbpage: meetingsData = {} } = meetings;
  const isLoading = useSelector((state) => state.pageContents?.loading);

  // Local state
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogPaywallOpen, setDialogPaywallOpen] = useState(false);

  // Categories of meetings
  const [nextUpcomingMeeting, setNextUpcomingMeeting] = useState(null);
  const [otherUpcomingMeetings, setOtherUpcomingMeetings] = useState([]);
  const [previousMeetings, setPreviousMeetings] = useState([]);

  // Pagination for previous meetings
  const [currentPageData, setCurrentPageData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const totalItems = previousMeetings?.length || 0;

  // Process meetings data when it changes
  useEffect(() => {
    /** get meeting data */
    const _asyncFetch = async () => {
      const res = await getMeetings();
      if (res?.data?.data) {
        organizeMeetings(res.data?.data);
      }
    };
    if (meetings) {
      _asyncFetch();
    }
  }, [meetings]);

  // Update pagination for previous meetings
  useEffect(() => {
    setCurrentPageData(
      getPaginatedItems(previousMeetings || [], currentPage, itemsPerPage),
    );
  }, [previousMeetings, currentPage]);

  // Organize meetings into categories
  const organizeMeetings = (meetingsData) => {
    const now = new Date();

    // Split meetings into upcoming and previous
    const upcoming = meetingsData
      .filter((meeting) => new Date(meeting.meeting_start_dt) > now)
      .sort(
        (a, b) => new Date(a.meeting_start_dt) - new Date(b.meeting_start_dt),
      );

    const previous = meetingsData
      .filter((meeting) => new Date(meeting.meeting_start_dt) <= now)
      .sort(
        (a, b) => new Date(b.meeting_start_dt) - new Date(a.meeting_start_dt),
      );

    // Set the next upcoming meeting (first in the sorted upcoming array)
    setNextUpcomingMeeting(upcoming.length > 0 ? upcoming[0] : null);

    // Set other upcoming meetings (all except the first)
    setOtherUpcomingMeetings(upcoming.slice(1));

    // Set previous meetings
    setPreviousMeetings(previous);
  };

  // Handle pagination
  const handlePageChange = (pageNumber) => {
    setCurrentPageData(
      getPaginatedItems(previousMeetings || [], pageNumber, itemsPerPage),
    );
    setCurrentPage(pageNumber);
  };

  // Meeting details dialog handlers
  const handleOpenDetails = (meeting) => {
    setSelectedMeeting(meeting);
    setDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDialogOpen(false);
  };

  // Print meeting details
  const handlePrint = () => {
    const printContent = printRef.current;
    //const originalContents = document.body.innerHTML;

    if (printContent) {
      const printCSS = `
        <style>
          @media print {
            body { font-family: Arial, sans-serif; }
            img { max-width: 100%; }
            .meeting-print-header { text-align: center; margin-bottom: 20px; }
            .meeting-print-content { margin: 20px 0; }
            .meeting-print-dates { color: #666; margin: 10px 0; }
            .meeting-print-location { color: #666; margin: 10px 0; }
          }
        </style>
      `;

      const printWindow = window.open("", "_blank");
      printWindow.document.write("<html><head><title>Meeting Details</title>");
      printWindow.document.write(printCSS);
      printWindow.document.write("</head><body>");
      printWindow.document.write('<div class="meeting-print-container">');
      printWindow.document.write('<div class="meeting-print-header">');
      printWindow.document.write(`<h1>${selectedMeeting.meeting_title}</h1>`);
      printWindow.document.write("</div>");

      // Dates
      printWindow.document.write('<div class="meeting-print-dates">');
      printWindow.document.write(
        `<p><strong>Dates:</strong> ${formatMeetingDate(selectedMeeting.meeting_start_dt)} to ${formatMeetingDate(selectedMeeting.meeting_end_dt)}</p>`,
      );
      printWindow.document.write("</div>");

      // Location if available
      if (selectedMeeting.meeting_location) {
        printWindow.document.write('<div class="meeting-print-location">');
        printWindow.document.write(
          `<p><strong>Location:</strong> ${selectedMeeting.meeting_location}</p>`,
        );
        printWindow.document.write("</div>");
      }

      // Meeting content
      printWindow.document.write('<div class="meeting-print-content">');
      printWindow.document.write(selectedMeeting.meeting_content);
      printWindow.document.write("</div>");

      printWindow.document.write("</div></body></html>");
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  // File download handler for meeting documents
  const handleDownloadDocument = (meetingId, documentName) => {
    getMeetingDocument(meetingId, documentName)
      .then((blob) => {
        // Create a blob URL and initiate download
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = documentName;
        document.body.appendChild(link);
        link.click();

        // Cleanup
        window.URL.revokeObjectURL(link.href);
        document.body.removeChild(link);
      })
      .catch((error) => {
        log.error("Error downloading document:", error);
      });
  };

  // Format date time for display
  const formatMeetingDate = (dateTime) => {
    if (!dateTime) return "";

    try {
      const date = new Date(dateTime);

      // Format: "Monday, Jan 1, 2024 • 2:30 PM"
      return new Intl.DateTimeFormat("en-US", {
        weekday: "long",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      }).format(date);
    } catch (e) {
      log.error("Error formatting date:", e);
      return dateTime;
    }
  };

  // Get calendar event data for meeting
  const getCalendarEvent = (meeting) => {
    if (!meeting) {
      return null;
    }

    const startDateObj = new Date(meeting.meeting_start_dt);
    const endDateObj = new Date(meeting.meeting_end_dt);

    // Calculate duration in minutes, default to 60 if invalid
    let duration = 60; // Default duration
    if (
      !isNaN(startDateObj) &&
      !isNaN(endDateObj) &&
      endDateObj > startDateObj
    ) {
      duration = (endDateObj.getTime() - startDateObj.getTime()) / (1000 * 60);
    }

    return {
      title: meeting.meeting_title,
      description: meeting.meeting_content
        ? meeting.meeting_content.replace(/<[^>]+>/g, " ").substring(0, 300)
        : "",
      location: meeting.meeting_location || "",
      startDate: meeting.meeting_start_dt, // Use startDate (as string or Date object)
      duration: duration, // Provide duration in minutes
      id: meeting.meeting_id, // id for ICS filename
    };
  };

  // Membership logic for dialog (reuse same as above)
  const { data: userData = {} } = useSelector((state) => state.user);
  const requireMembership = true;
  const hasMembershipVal = !requireMembership ? true : hasMembership(userData);
  const auth = useSelector((state) => state.auth);

  return (
    <PageLayout pageName="Meetings" showLoader={isLoading} checkComingSoon>
      {/* Banner */}
      {meetingsData?.meetingbanner ? (
        <PageBanner
          bannerImage={meetingsData.meetingbanner.banner_image}
          mainTitle={meetingsData.meetingbanner.banner_title || "Meetings"}
          pageTitle="Meeting"
        />
      ) : (
        <PageBanner mainTitle="Meetings" pageTitle="Meeting" />
      )}

      {/* Next Upcoming Meeting Section */}
      {nextUpcomingMeeting && (
        <Box component="section" sx={{ py: 6 }}>
          <Container>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 4,
              }}
            >
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                align="center"
                sx={{ mb: 0, mr: 2 }}
                className="meeting-title"
              >
                Next Upcoming Meeting
              </Typography>
              <Chip
                label="UPCOMING"
                color="primary"
                size="small"
                sx={{ fontWeight: "bold" }}
              />
            </Box>

            <MeetingCard
              meeting={nextUpcomingMeeting}
              variant="featured"
              onViewDetails={handleOpenDetails}
              onDownloadDocument={handleDownloadDocument}
              formatDate={formatMeetingDate}
              getCalendarEvent={getCalendarEvent}
            />
          </Container>
        </Box>
      )}

      {/* Other Upcoming Meetings Section */}
      {otherUpcomingMeetings.length > 0 && (
        <Box component="section" sx={{ py: 4, bgcolor: "#f5f8ff" }}>
          <Container>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              align="center"
              sx={{ mb: 4 }}
              className="meeting-title"
            >
              Other Upcoming Meetings
            </Typography>

            <Box>
              {otherUpcomingMeetings.map((meeting, index) => (
                <MeetingCard
                  key={index}
                  meeting={meeting}
                  variant="upcoming"
                  onViewDetails={handleOpenDetails}
                  onDownloadDocument={handleDownloadDocument}
                  formatDate={formatMeetingDate}
                  getCalendarEvent={getCalendarEvent}
                />
              ))}
            </Box>
          </Container>
        </Box>
      )}

      {/* Previous Meetings Section */}
      <Box component="section" sx={{ py: 4, bgcolor: "#f9fbfc" }}>
        <Container>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            align="center"
            sx={{ mb: 4 }}
            className="meeting-title"
          >
            Previous Meetings
          </Typography>

          {currentPageData.length > 0 ? (
            <Box>
              {currentPageData.map((meeting, index) => (
                <MeetingCard
                  key={index}
                  meeting={meeting}
                  variant="past"
                  onViewDetails={handleOpenDetails}
                  onDownloadDocument={handleDownloadDocument}
                  formatDate={formatMeetingDate}
                  getCalendarEvent={getCalendarEvent}
                />
              ))}

              {/* Pagination */}
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <Pagination
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  initialPage={currentPage}
                  className={"page-margin"}
                  onChange={handlePageChange}
                />
              </Box>
            </Box>
          ) : isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary">
                No previous meetings available.
              </Typography>
            </Paper>
          )}
        </Container>
      </Box>

      {/* Meeting Details Dialog */}
      <Dialog
        fullScreen={fullScreen}
        maxWidth={
          selectedMeeting && isRecapAvailable(selectedMeeting) ? "xl" : "lg"
        }
        fullWidth
        open={dialogOpen}
        onClose={handleCloseDetails}
        scroll="paper"
        aria-labelledby="meeting-details-dialog"
      >
        <DialogTitle id="meeting-details-dialog">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" component="div">
              {selectedMeeting?.meeting_title}
            </Typography>
            <Box>
              {selectedMeeting &&
                new Date(selectedMeeting.meeting_start_dt) > new Date() && (
                  <Tooltip title="Add to calendar">
                    <IconButton color="primary" sx={{ mr: 1 }}>
                      <CalendarAdd
                        event={
                          selectedMeeting
                            ? getCalendarEvent(selectedMeeting)
                            : null
                        }
                        iconOnly={true}
                      />
                    </IconButton>
                  </Tooltip>
                )}
              <Tooltip title="Print">
                <IconButton
                  onClick={handlePrint}
                  color="primary"
                  sx={{ mr: 1 }}
                >
                  <PrintIcon />
                </IconButton>
              </Tooltip>
              <IconButton
                onClick={handleCloseDetails}
                color="inherit"
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedMeeting && (
            <Box ref={printRef}>
              {/* Status Chip */}
              <Box sx={{ mb: 3 }}>
                {new Date(selectedMeeting.meeting_start_dt) > new Date() ? (
                  <Chip label="UPCOMING" color="primary" />
                ) : (
                  <Chip label="PAST" color="secondary" />
                )}
              </Box>

              {/* Date and Location */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 2,
                    color: "text.secondary",
                  }}
                >
                  <CalendarMonthIcon sx={{ mr: 1 }} />
                  <Typography variant="body1">
                    {formatMeetingDate(selectedMeeting.meeting_start_dt)} to{" "}
                    {formatMeetingDate(selectedMeeting.meeting_end_dt)}
                  </Typography>
                </Box>

                {selectedMeeting.meeting_location && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                      color: "text.secondary",
                    }}
                  >
                    <PlaceIcon sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      {selectedMeeting.meeting_location}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Meeting Image */}
              {selectedMeeting.meeting_image && (
                <Box sx={{ mb: 3, textAlign: "center" }}>
                  <img
                    src={`${process.env.REACT_APP_IMAGE_URL}/${selectedMeeting.meeting_image}`}
                    alt={selectedMeeting.meeting_title}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "400px",
                      borderRadius: "8px",
                    }}
                  />
                </Box>
              )}

              {/* Meeting Content */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body1"
                  component="div"
                  sx={{
                    "& img": { maxWidth: "100%", height: "auto" },
                    "& a": { color: "primary.main" },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: selectedMeeting.meeting_content,
                  }}
                />
              </Box>

              {/* Recap Video (if available and past) */}
              {isRecapAvailable(selectedMeeting) &&
                (hasMembershipVal ? (
                  <Box sx={{ mb: 3, px: { xs: 0, sm: 2, md: 6 } }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Meeting Recap Video
                    </Typography>
                    <VideoEmbed
                      urlOrEmbed={selectedMeeting.meeting_video}
                      title="Meeting Recap Video"
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      mb: 3,
                      px: { xs: 0, sm: 2, md: 6 },
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Button
                      startIcon={<PlayCircleIcon />}
                      variant="outlined"
                      color="success"
                      onClick={() => setDialogPaywallOpen(true)}
                    >
                      Meeting Recap videos are a members-only feature. Sign up
                      or log in to watch.
                    </Button>
                  </Box>
                ))}

              {/* Document Download */}
              {selectedMeeting.meeting_documents && (
                <Box sx={{ mt: 4 }}>
                  <Button
                    startIcon={<PictureAsPdfIcon />}
                    variant="outlined"
                    color="primary"
                    onClick={() =>
                      handleDownloadDocument(
                        selectedMeeting.meeting_id,
                        selectedMeeting.meeting_documents,
                      )
                    }
                  >
                    Download Meeting Document
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedMeeting &&
            new Date(selectedMeeting.meeting_start_dt) > new Date() && (
              <Button
                component={Link}
                to={`/register?year=${toString(new Date(selectedMeeting.meeting_start_dt).getFullYear())}`}
                variant="contained"
                color="primary"
                onClick={handleCloseDetails}
              >
                Register for this Meeting
              </Button>
            )}
          <Button onClick={handleCloseDetails} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Paywall Dialog */}
      <UpsellOrLogin
        open={dialogPaywallOpen}
        onClose={() => setDialogPaywallOpen(false)}
        isAuthenticated={auth?.isAuthenticated}
        requireMembership={requireMembership}
        hasMembership={hasMembershipVal}
        featureName="Meeting Recap Video"
      />
    </PageLayout>
  );
};

export default Meeting;
